import { Maximize2, PhoneOff } from 'lucide-react';
import { Avatar, Button } from '@cyphex/ui';

export interface CallPipBarProps {
  peerName: string;
  /** The callee has not picked up yet. */
  ringing: boolean;
  /** Go back to the full call surface. */
  onReturn: () => void;
  onHangup: () => void;
}

/**
 * The call, shrunk to a corner tile, while you get on with something else.
 *
 * This is what makes leaving the call route survivable. The media surface is
 * still mounted — hidden, not unmounted, so audio keeps flowing and the SFU
 * never sees you leave — and this tile is the handle back to it. Teams does the
 * same thing for the same reason: hanging up on somebody because their
 * colleague sent you a message would be an absurd way to lose a call.
 *
 * It carries exactly two controls. RETURN, because the tile's whole job is to
 * be the way back, and HANG UP, because it is the one action you must never
 * have to go looking for. Mute deliberately is not here: it lives on the call
 * surface next to the device picker that explains it, and a mute button whose
 * state you cannot see is a worse offer than no mute button at all.
 *
 * Bottom LEFT, while message and incoming-call popups own the bottom right.
 * They are both persistent and both anchored to a corner, so sharing one would
 * mean a ringing call could bury the call you are already on.
 */
export function CallPipBar({ peerName, ringing, onReturn, onHangup }: CallPipBarProps) {
  return (
    <div
      className="pointer-events-auto flex items-center gap-3 rounded-xl border border-border bg-card p-2.5 pr-3 shadow-lg"
      role="region"
      aria-label={`Call with ${peerName}`}
    >
      <Avatar name={peerName} size="md" />

      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">{peerName}</p>
        {/* aria-live so a screen reader hears the pick-up without the tile
            having to steal focus from whatever is being read. */}
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {ringing ? 'Ringing…' : 'Connected'}
        </p>
      </div>

      <div className="flex items-center gap-1.5 pl-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onReturn}
          aria-label="Return to call"
          title="Return to call"
        >
          <Maximize2 size={16} />
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={onHangup}
          aria-label="Hang up"
          title="Hang up"
        >
          <PhoneOff size={16} />
        </Button>
      </div>
    </div>
  );
}
